import { defaultAxiosInstance } from '../../../api/defaultInstance';

/**
 * 지갑 대시보드 조회
 * @param {string|undefined} from - ISO 날짜 문자열
 * @param {string|undefined} to - ISO 날짜 문자열
 * @returns {Promise<{kpi, pending, alerts}>}
 */
export async function fetchWalletDashboard(from, to) {
  try {
    const params = {};
    if (from && to) {
      params.from = from;
      params.to = to;
    }

    const response = await defaultAxiosInstance.get('/admin/wallet/dashboard', { params });
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchWalletDashboard 오류:', error);
    throw error;
  }
}

/**
 * 지갑 충전 목록 조회
 * @param {Object} params - 조회 파라미터
 * @param {string} [params.userQuery] - 사용자 검색어
 * @param {string} [params.chargeAccountId] - 충전 계정 ID
 * @param {string} [params.status] - 상태 필터 ('NOT_ISSUED'|'CANCELED'|'WAITING_DEPOSIT'|'DEPOSIT_CONFIRMED'|'EXPIRED')
 * @param {string} [params.expireFrom] - 만료 시작일 (ISO)
 * @param {string} [params.expireTo] - 만료 종료일 (ISO)
 * @param {number} [params.page] - 페이지 번호
 * @param {number} [params.size] - 페이지 크기
 * @returns {Promise<{list, totalCount, hasMore}>}
 */
export async function fetchWalletCharges(params = {}) {
  try {
    const queryParams = {};

    // falsy 값 제외하고 쿼리 파라미터 구성
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams[key] = params[key];
      }
    });

    const response = await defaultAxiosInstance.get('/admin/wallet/charges', { params: queryParams });
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchWalletCharges 오류:', error);
    throw error;
  }
}

/**
 * 지갑 결제 목록 조회
 * @param {Object} params - 조회 파라미터
 * @param {string} [params.userQuery] - 사용자 검색어
 * @param {string} [params.orderId] - 주문 ID
 * @param {string} [params.status] - 상태 필터
 * @param {string} [params.fromDate] - 시작일 (ISO)
 * @param {string} [params.toDate] - 종료일 (ISO)
 * @param {number} [params.page] - 페이지 번호 (기본값: 1)
 * @param {number} [params.size] - 페이지 크기 (기본값: 50)
 * @returns {Promise<{list, totalCount, hasMore}>}
 */
export async function fetchWalletPayments(params = {}) {
  try {
    const queryParams = {};

    // falsy 값 제외하고 쿼리 파라미터 구성
    Object.keys(params).forEach(key => {
      if (params[key]) {
        queryParams[key] = params[key];
      }
    });

    const response = await defaultAxiosInstance.get('/admin/wallet/payments', { params: queryParams });
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchWalletPayments 오류:', error);
    throw error;
  }
}

/**
 * 결제 취소 가능 여부 및 취소 가능 항목 조회
 * @param {string} adPaymentTransactionId - 광고 결제 트랜잭션 ID
 * @returns {Promise}
 */
export async function fetchCancelable(adPaymentTransactionId) {
  try {
    const response = await defaultAxiosInstance.get(
      `/admin/wallet/payments/${adPaymentTransactionId}/cancelable`
    );
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchCancelable 오류:', error);
    throw error;
  }
}

/**
 * 결제 취소 실행
 * @param {string} adPaymentTransactionId - 광고 결제 트랜잭션 ID
 * @param {Array} items - 취소할 항목 배열
 * @param {string} reason - 취소 사유
 * @param {string} [idempotencyKey] - 멱등성 키
 * @returns {Promise}
 */
export async function executeCancel(adPaymentTransactionId, items, reason, idempotencyKey) {
  try {
    const config = {};
    if (idempotencyKey) {
      config.headers = { 'idempotency-key': idempotencyKey };
    }

    const response = await defaultAxiosInstance.post(
      `/admin/wallet/payments/${adPaymentTransactionId}/cancel`,
      { items, reason },
      config
    );
    return response.data;
  } catch (error) {
    console.error('walletService.executeCancel 오류:', error);
    throw error;
  }
}

/**
 * 포인트 출금 목록 조회
 * @param {Object} params - 조회 파라미터
 * @param {string} [params.status] - 상태 필터
 * @param {string} [params.deadlineFilter] - 마감 필터
 * @param {string} [params.userId] - 사용자 ID
 * @param {string} [params.from] - 시작일 (ISO)
 * @param {string} [params.to] - 종료일 (ISO)
 * @param {number} [params.limit] - 조회 제한 (기본값: 30)
 * @param {number} [params.offset] - 오프셋 (기본값: 0)
 * @returns {Promise<{items, total, limit, offset}>}
 */
export async function fetchWithdrawals(params = {}) {
  try {
    const queryParams = {};

    // 기본값 설정
    const finalParams = {
      limit: 30,
      offset: 0,
      ...params
    };

    // falsy 값 제외하고 쿼리 파라미터 구성
    Object.keys(finalParams).forEach(key => {
      if (finalParams[key] || finalParams[key] === 0) {
        queryParams[key] = finalParams[key];
      }
    });

    const response = await defaultAxiosInstance.get('/admin/point/withdrawal', { params: queryParams });
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchWithdrawals 오류:', error);
    throw error;
  }
}

/**
 * 포인트 출금 상세 조회
 * @param {string} id - 출금 ID
 * @returns {Promise}
 */
export async function fetchWithdrawalDetail(id) {
  try {
    const response = await defaultAxiosInstance.get(`/admin/point/withdrawal/${id}`);
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchWithdrawalDetail 오류:', error);
    throw error;
  }
}

/**
 * 포인트 출금 승인
 * @param {string} id - 출금 ID
 * @param {string} [restrictedReason] - 제한 사유 (선택사항)
 * @returns {Promise}
 */
export async function approveWithdrawal(id, restrictedReason) {
  try {
    const body = {};
    if (restrictedReason) {
      body.restrictedReason = restrictedReason;
    }

    const response = await defaultAxiosInstance.post(
      `/admin/point/withdrawal/${id}/approve`,
      body
    );
    return response.data;
  } catch (error) {
    console.error('walletService.approveWithdrawal 오류:', error);
    throw error;
  }
}

/**
 * 포인트 출금 거절
 * @param {string} id - 출금 ID
 * @param {string} rejectReason - 거절 사유
 * @returns {Promise}
 */
export async function rejectWithdrawal(id, rejectReason) {
  try {
    const response = await defaultAxiosInstance.post(
      `/admin/point/withdrawal/${id}/reject`,
      { rejectReason }
    );
    return response.data;
  } catch (error) {
    console.error('walletService.rejectWithdrawal 오류:', error);
    throw error;
  }
}

/**
 * 포인트 출금 완료
 * @param {string} id - 출금 ID
 * @returns {Promise}
 */
export async function completeWithdrawal(id) {
  try {
    const response = await defaultAxiosInstance.post(
      `/admin/point/withdrawal/${id}/complete`,
      {}
    );
    return response.data;
  } catch (error) {
    console.error('walletService.completeWithdrawal 오류:', error);
    throw error;
  }
}

/**
 * 지갑 사용자 상세 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise}
 */
export async function fetchWalletUser(userId) {
  try {
    const response = await defaultAxiosInstance.get(`/admin/wallet/users/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchWalletUser 오류:', error);
    throw error;
  }
}

export async function searchWalletUser(q) {
  try {
    const response = await defaultAxiosInstance.get('/admin/wallet/search', { params: { q } });
    return response.data.data;
  } catch (error) {
    console.error('walletService.searchWalletUser 오류:', error);
    throw error;
  }
}

/**
 * 수동 포인트 충전
 * @param {string} userId - 사용자 ID
 * @param {Object} data - 충전 데이터
 * @param {string} data.pointType - 포인트 타입
 * @param {number} data.amount - 충전 금액
 * @param {string} data.reason - 충전 사유
 * @param {string} [data.restrictedReason] - 제한 사유 (선택사항)
 * @param {string} [idempotencyKey] - 멱등성 키
 * @returns {Promise}
 */
export async function manualCharge(userId, data, idempotencyKey) {
  try {
    const { pointType, amount, reason, restrictedReason } = data;
    const body = { pointType, amount, reason };

    if (restrictedReason) {
      body.restrictedReason = restrictedReason;
    }

    const config = {};
    if (idempotencyKey) {
      config.headers = { 'idempotency-key': idempotencyKey };
    }

    const response = await defaultAxiosInstance.post(
      `/admin/wallet/users/${userId}/manual-charge`,
      body,
      config
    );
    return response.data;
  } catch (error) {
    console.error('walletService.manualCharge 오류:', error);
    throw error;
  }
}

/**
 * CS 메모 추가
 * @param {string} userId - 사용자 ID
 * @param {string} content - 메모 내용
 * @returns {Promise}
 */
export async function addCsMemo(userId, content) {
  try {
    const response = await defaultAxiosInstance.post(
      `/admin/wallet/users/${userId}/memos`,
      { content }
    );
    return response.data;
  } catch (error) {
    console.error('walletService.addCsMemo 오류:', error);
    throw error;
  }
}

/**
 * CS 메모 목록 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise}
 */
export async function getCsMemos(userId) {
  try {
    const response = await defaultAxiosInstance.get(
      `/admin/wallet/users/${userId}/memos`
    );
    return response.data.data;
  } catch (error) {
    console.error('walletService.getCsMemos 오류:', error);
    throw error;
  }
}

/**
 * 리스크 플래그 설정
 * @param {string} userId - 사용자 ID
 * @param {string} status - 플래그 상태
 * @param {string} reason - 설정 사유
 * @returns {Promise}
 */
export async function setRiskFlag(userId, status, reason) {
  try {
    const response = await defaultAxiosInstance.put(
      `/admin/wallet/users/${userId}/risk-flag`,
      { status, reason }
    );
    return response.data;
  } catch (error) {
    console.error('walletService.setRiskFlag 오류:', error);
    throw error;
  }
}

/**
 * 포인트 결제 취소 (운영자 대신)
 * @param {number} userId - 사용자 ID
 * @param {number} adPaymentTransactionId - 결제 트랜잭션 ID
 * @param {string} reason - 취소 사유
 * @param {string} [idempotencyKey] - 멱등성 키
 * @returns {Promise}
 */
export async function pointPaymentCancel(userId, adPaymentTransactionId, reason, idempotencyKey) {
  try {
    const config = {};
    if (idempotencyKey) {
      config.headers = { 'Idempotency-Key': idempotencyKey };
    }
    const response = await defaultAxiosInstance.post(
      '/admin/point/payment/cancel',
      { userId, adPaymentTransactionId, reason },
      config
    );
    return response.data;
  } catch (error) {
    console.error('walletService.pointPaymentCancel 오류:', error);
    throw error;
  }
}

/**
 * 지갑 정책 목록 조회
 * @returns {Promise<{policies}>}
 */
export async function fetchPolicies() {
  try {
    const response = await defaultAxiosInstance.get('/admin/wallet/policies');
    return response.data.data;
  } catch (error) {
    console.error('walletService.fetchPolicies 오류:', error);
    throw error;
  }
}

/**
 * 지갑 정책 업데이트
 * @param {string} policyKey - 정책 키
 * @param {*} value - 정책 값
 * @param {string} reason - 변경 사유
 * @returns {Promise<{policyKey, beforeValue, afterValue, changedAt}>}
 */
export async function updatePolicy(policyKey, value, reason) {
  try {
    const response = await defaultAxiosInstance.put(
      `/admin/wallet/policies/${policyKey}`,
      { value, reason }
    );
    return response.data.data;
  } catch (error) {
    console.error('walletService.updatePolicy 오류:', error);
    throw error;
  }
}
